import { useRef, useState } from 'react';
import { ImagePlus, Minus, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { posterT, PORTRAIT_FRAME, DEFAULT_POSTER_IMAGE } from './poster-design';
import { blobToDataUrl } from './PosterArtwork';

export interface PortraitCrop {
  zoom: number;
  x: number;
  y: number;
}

export interface PortraitSource {
  url: string;
  width: number;
  height: number;
  crop: PortraitCrop;
}

export const DEFAULT_PORTRAIT_CROP: PortraitCrop = { zoom: 1, x: 0, y: 0 };

export function portraitGeometry(source: PortraitSource, crop: PortraitCrop) {
  const scale = Math.max(
    PORTRAIT_FRAME.width / source.width,
    PORTRAIT_FRAME.height / source.height
  );
  const width = source.width * scale * crop.zoom;
  const height = source.height * scale * crop.zoom;
  const limitX = (width - PORTRAIT_FRAME.width) / 2;
  const limitY = (height - PORTRAIT_FRAME.height) / 2;
  const x = Math.max(-limitX, Math.min(limitX, crop.x));
  const y = Math.max(-limitY, Math.min(limitY, crop.y));
  return { width, height, x, y };
}

export function PosterPortraitEditor({
  source: initialSource,
  onCancel,
  onApply,
  onRemove,
}: {
  source: PortraitSource;
  onCancel: () => void;
  onRemove?: () => void;
  onApply: (url: string, source: PortraitSource) => void;
}) {
  const [source, setSource] = useState(initialSource);
  const [crop, setCrop] = useState(initialSource.crop);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const busy = saving || loading;
  const fileInput = useRef<HTMLInputElement>(null);
  const replaceSource = async (file: File | null, restore = false) => {
    if (!file && !restore) return;
    setLoading(true);
    setError(null);
    try {
      if (
        file &&
        (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
          file.size > 10 * 1024 * 1024)
      ) {
        setError(posterT('clinicalTrials.posterEditor.imageError'));
        return;
      }
      const url = file ? await blobToDataUrl(file) : DEFAULT_POSTER_IMAGE;
      const image = new Image();
      image.src = url;
      await image.decode();
      setSource({
        url,
        width: image.naturalWidth,
        height: image.naturalHeight,
        crop: DEFAULT_PORTRAIT_CROP,
      });
      setCrop(DEFAULT_PORTRAIT_CROP);
      drag.current = null;
    } catch {
      setError(posterT('clinicalTrials.posterPortrait.error'));
    } finally {
      setLoading(false);
    }
  };
  const drag = useRef<{ x: number; y: number; crop: PortraitCrop } | null>(null);
  const geometry = portraitGeometry(source, crop);
  const change = (next: PortraitCrop) => {
    const bounded = portraitGeometry(source, next);
    setCrop({ zoom: next.zoom, x: bounded.x, y: bounded.y });
  };
  const zoom = (value: number) => change({ ...crop, zoom: Math.max(1, Math.min(3, value)) });
  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      const image = new Image();
      image.src = source.url;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = PORTRAIT_FRAME.width * 3;
      canvas.height = PORTRAIT_FRAME.height * 3;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.scale(3, 3);
      context.drawImage(
        image,
        (PORTRAIT_FRAME.width - geometry.width) / 2 + geometry.x,
        (PORTRAIT_FRAME.height - geometry.height) / 2 + geometry.y,
        geometry.width,
        geometry.height
      );
      onApply(canvas.toDataURL('image/png'), { ...source, crop });
    } catch {
      setError(posterT('clinicalTrials.posterPortrait.error'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open && !busy) onCancel();
      }}
    >
      <DialogContent className="flex max-h-[90dvh] max-w-md flex-col gap-0 overflow-hidden rounded-3xl p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{posterT('clinicalTrials.posterPortrait.title')}</DialogTitle>
          <DialogDescription>{posterT('clinicalTrials.posterPortrait.hint')}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 space-y-4 overflow-y-auto px-6 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              className="gap-2"
              onClick={() => fileInput.current?.click()}
            >
              <ImagePlus className="h-4 w-4" />
              {posterT('clinicalTrials.posterPortrait.replace')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={
                busy ||
                (source.url === DEFAULT_POSTER_IMAGE &&
                  crop.zoom === 1 &&
                  crop.x === 0 &&
                  crop.y === 0)
              }
              onClick={() => void replaceSource(null, true)}
            >
              {posterT('clinicalTrials.posterEditor.removeImage')}
            </Button>
            {onRemove && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                className="gap-2 text-destructive hover:text-destructive"
                onClick={onRemove}
              >
                <Trash2 className="h-4 w-4" />
                {posterT('clinicalTrials.posterPortrait.remove')}
              </Button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-label={posterT('clinicalTrials.ui.chooseImage')}
              className="sr-only"
              disabled={busy}
              onChange={event => {
                void replaceSource(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
          </div>
          <div className="flex justify-center rounded-3xl bg-muted/50 py-4">
            <div
              role="img"
              aria-label={posterT('clinicalTrials.posterPortrait.move')}
              tabIndex={0}
              data-portrait-crop
              className="relative touch-none select-none overflow-hidden rounded-[50%] bg-white shadow-md outline-none ring-1 ring-border focus-visible:ring-2 focus-visible:ring-primary cursor-grab active:cursor-grabbing"
              style={{ width: PORTRAIT_FRAME.width, height: PORTRAIT_FRAME.height }}
              onPointerDown={event => {
                if (busy) return;
                event.currentTarget.setPointerCapture(event.pointerId);
                drag.current = { x: event.clientX, y: event.clientY, crop };
              }}
              onPointerMove={event => {
                if (!drag.current) return;
                const scale =
                  PORTRAIT_FRAME.width / event.currentTarget.getBoundingClientRect().width;
                change({
                  ...drag.current.crop,
                  x: drag.current.crop.x + (event.clientX - drag.current.x) * scale,
                  y: drag.current.crop.y + (event.clientY - drag.current.y) * scale,
                });
              }}
              onPointerUp={() => {
                drag.current = null;
              }}
              onPointerCancel={() => {
                drag.current = null;
              }}
              onLostPointerCapture={() => {
                drag.current = null;
              }}
              onKeyDown={event => {
                if (
                  busy ||
                  !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
                )
                  return;
                event.preventDefault();
                const step = event.shiftKey ? 20 : 5;
                change({
                  ...crop,
                  x:
                    crop.x +
                    (event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0),
                  y:
                    crop.y +
                    (event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0),
                });
              }}
            >
              <img
                src={source.url}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: geometry.width,
                  height: geometry.height,
                  left: (PORTRAIT_FRAME.width - geometry.width) / 2 + geometry.x,
                  top: (PORTRAIT_FRAME.height - geometry.height) / 2 + geometry.y,
                }}
              />
            </div>
          </div>
          <fieldset disabled={busy} className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <label htmlFor="portrait-zoom">{posterT('clinicalTrials.posterPortrait.zoom')}</label>
              <output htmlFor="portrait-zoom" className="tabular-nums text-muted-foreground">
                {Math.round(crop.zoom * 100)}%
              </output>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={posterT('clinicalTrials.posterPortrait.zoomOut')}
                disabled={busy || crop.zoom <= 1}
                onClick={() => zoom(crop.zoom - 0.1)}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <input
                id="portrait-zoom"
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={crop.zoom}
                onChange={event => zoom(Number(event.target.value))}
                className="min-w-0 flex-1 accent-primary"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={posterT('clinicalTrials.posterPortrait.zoomIn')}
                disabled={busy || crop.zoom >= 3}
                onClick={() => zoom(crop.zoom + 0.1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => change(DEFAULT_PORTRAIT_CROP)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {posterT('clinicalTrials.posterPortrait.reset')}
            </Button>
          </fieldset>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t bg-background px-6 py-4">
          <Button variant="outline" disabled={busy} onClick={onCancel}>
            {posterT('clinicalTrials.posterPortrait.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void apply()}>
            {posterT('clinicalTrials.posterPortrait.apply')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
